import { motion, useTransform, useScroll, useSpring } from "framer-motion";
import React, { useEffect, useRef, useState } from "react";

import { useTheme } from "@mui/material";

import { getColor } from "@hooks";

import { ValuesSectionMainContainer } from "./styles.styled";

interface TracingBeamProps {
  children: React.ReactNode;
}

export const TracingBeam = ({ children }: TracingBeamProps) => {
  const theme = useTheme();

  const ref = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start 20%", "end 30%"],
  });

  const contentRef = useRef<HTMLDivElement>(null);
  const [svgHeight, setSvgHeight] = useState(0);

  useEffect(() => {
    const updateHeight = () => {
      if (contentRef.current) {
        setSvgHeight(contentRef.current.offsetHeight);
      }
    };

    updateHeight();

    window.addEventListener("resize", updateHeight);

    return () => {
      window.removeEventListener("resize", updateHeight);
    };
  }, []);

  const y1 = useSpring(
    useTransform(scrollYProgress, [0, 0.5, 1], [0, svgHeight, svgHeight]),
    {
      stiffness: 500,
      damping: 90,
    },
  );
  const y2 = useSpring(
    useTransform(scrollYProgress, [0, 0.5, 1], [0, svgHeight - 300, svgHeight]),
    {
      stiffness: 500,
      damping: 90,
    },
  );

  return (
    <ValuesSectionMainContainer ref={ref}>
      <div>
        <svg
          viewBox={`0 0 20 ${svgHeight}`}
          width="20"
          height={svgHeight}
          aria-hidden="true"
        >
          <motion.path
            d={`M 1 0 V-36 l 18 24 V${svgHeight * 0.8} l -18 24 V${svgHeight}`}
            fill="none"
            stroke={getColor(c => c.tokens.bg["soft-200"], theme)}
            strokeWidth="3"
            transition={{
              duration: 10,
            }}
          />

          <motion.path
            d={`M 1 0 V-36 l 18 24 V${svgHeight * 0.8} l -18 24 V${svgHeight}`}
            fill="none"
            stroke="url(#gradient)"
            strokeWidth="3"
            className="motion-reduce:hidden"
            transition={{
              duration: 10,
            }}
          />

          <defs>
            <motion.linearGradient
              id="gradient"
              gradientUnits="userSpaceOnUse"
              x1="0"
              x2="0"
              y1={y1}
              y2={y2}
            >
              <stop stopColor="#96C5F0" stopOpacity="0"></stop>
              <stop stopColor="#96C5F0"></stop>
              <stop offset="0.465" stopColor="#05DF54"></stop>
              <stop
                offset="1"
                stopColor={
                  theme.palette.mode === "dark" ? "#0E121B00" : "#F5F7FA00"
                }
              ></stop>
            </motion.linearGradient>
          </defs>
        </svg>
      </div>

      <div ref={contentRef}>{children}</div>
    </ValuesSectionMainContainer>
  );
};
